import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { PlotlyService, PlotlySharedModule } from 'angular-plotly.js';
import { Color, PlotData } from 'plotly.js';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, delay, filter, startWith } from 'rxjs/operators';
import { IGraphConfig } from 'src/app/model/graph-configs/graph-config';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ICard } from 'src/app/model/thingie/thingie-card';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { loadPlotly } from 'src/app/utility/plotly';
import { formatRelativeTimeWithHours } from 'src/app/utility/plotly/relative-time-tick-formatter';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { ContextTypeDirective } from '../../../directives/context-type/context-type.directive';
import { PureFunctionCallPipe } from '../../../utility/pipes/pure-member-call/pure-member-call.pipe';
import { TaskStatusBarComponent } from '../../task-status-bar/task-status-bar.component';

@Component({
  selector: 'app-card-plotly',
  templateUrl: './card-plotly.component.html',
  styleUrls: ['./card-plotly.component.scss'],
  imports: [PlotlySharedModule, NgTemplateOutlet, IonIcon, RouterLink, TaskStatusBarComponent, ContextTypeDirective, AsyncPipe, PureFunctionCallPipe]
})

export class CardPlotlyComponent implements AfterViewInit, OnInit, OnDestroy {
  protected readonly urls = DefaultUrls;

  @Input({ required: true })
  public card!: ICard;

  @Input({ required: true })
  public id!: string;

  @Output()
  public plotNextPlot = new EventEmitter<null>();

  public readyToPlot = false;
  public plotting = false;
  public plotComplete = false;

  private subscriptions = new SubSink();

  @ViewChildren('plotWrapper')
  private plotWrapperList!: QueryList<ElementRef<HTMLElement>>;

  // hovertemplate stuff
  @ViewChild('hovertemplate', { read: TemplateRef })
  protected hoverTemplateRef!: TemplateRef<any>;

  protected hoverTemplateOverlayRef?: OverlayRef;

  protected readonly hovertemplateContextType!: {
    hoverinfo: {
      name: string;
      color?: Color;
      time: string;
      value: string;
      unit?: string;
    };
  };

  protected hovertemplatePositionStrategy = this.overlay
    .position()
    .flexibleConnectedTo({ x: 0, y: 0 })
    .withPositions([{
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    }, {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    }]);

  protected hovertemplateData: CardPlotlyComponent['hovertemplateContextType']['hoverinfo'] = {
    name: '',
    color: '',
    time: '',
    value: ''
  };

  protected hoverEvent = new Subject<{
    type: 'hover' | 'unhover';
    id: string; evt: {
      event: MouseEvent;
      points: {
        data: PlotData;
        x: any;
        y: any;
      }[];
    };
  }>();

  private plotlyInstance?: any;

  constructor(
    private thingieService: ThingieService,
    private plotly: PlotlyService,
    private router: Router,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    void loadPlotly();
  }

  public ngAfterViewInit() {
    this.setupHovertemplate();

    // whenever the list of plotwrappers changes, try to plot the next plot.
    this.subscriptions.sink = this.plotWrapperList.changes
      .pipe(
        // also try to plot once at the beginning
        startWith(undefined),
        // plot on another microtask, to combat NG0100 errors
        delay(0)
      )
      .subscribe(() => {
        this.readyToPlot = this.plotWrapperList.length > 0;
        this.plotNextPlot.emit();
      });
  }

  public ngOnInit(): void {
    this.subscriptions.sink = this
      .router
      .events
      .pipe(
        filter(ev => ev instanceof NavigationEnd),
      )
      .subscribe(() => this.unhover(null, 'all'));
  }

  public ngOnDestroy(): void {
    this.unhover(null, '');
    this.disposeHoverTemplateRef();
    this.subscriptions.unsubscribe();
  }

  private disposeHoverTemplateRef() {
    this.hoverTemplateOverlayRef?.dispose();
  }

  protected has3DTraces(graphConfig: IGraphConfig) {
    const series = graphConfig.internalSeriesList;
    return series.map(s => s.valueConfig.type === 'arrayValues').includes(true);
  }

  protected getThingiesByIds = (ids: string[]): Observable<IThingie[]> => {
    if (ids.length === 0) {
      return of([]);
    }
    return combineLatest(ids.map(id => this.thingieService.getThingieById(id)));
  };

  private setupHovertemplate() {
    this.subscriptions.sink = this.hoverEvent.pipe(
      debounceTime(10)
    ).subscribe(({ type, evt }) => {
      if (type === 'hover') {
        if (this.plotlyInstance === undefined) {
          return;
        }


        if (this.hoverTemplateOverlayRef === undefined) {
          this.hoverTemplateOverlayRef = this.overlay.create({
            hasBackdrop: false,
            positionStrategy: this.hovertemplatePositionStrategy
          });

          this.hoverTemplateOverlayRef.attach(new TemplatePortal<CardPlotlyComponent['hovertemplateContextType']>(
            this.hoverTemplateRef,
            this.viewContainerRef,
            { hoverinfo: this.hovertemplateData }
          ));
        }

        this.hovertemplatePositionStrategy.setOrigin({
          x: evt.event.clientX - 5,
          y: evt.event.clientY - 5,
          width: 10,
          height: 10
        });
        this.hoverTemplateOverlayRef.updatePosition();

        // calculate x and y labels using plotly methods for consistency - a little intricate, but works

        const fullLayout = this.plotlyInstance._fullLayout;
        fullLayout.xaxis.tickformatstops = this.plotlyInstance.layout.xaxis.tickformatstops;

        const point = evt.points[0];
        const _point = point as any;

        // setup y axis precision - 2 digits past largest digit of boundary
        const yaxis = fullLayout[_point.yaxis._name];
        const rng = yaxis.range.map(yaxis.r2d || Number);

        let precision = this.precisionFn((rng[1] - rng[0]) / 2);
        if (precision === Infinity || precision === -Infinity || Number.isNaN(precision)) {
          precision = this.precisionFn(Math.max(Math.abs(rng[1]), Math.abs(rng[0])));
        }
        if (precision === Infinity || precision === -Infinity || Number.isNaN(precision)) {
          precision = 2;
        }
        yaxis.tickformat = `.${precision}f`;

        const trace = _point.fullData;
        const labels = trace._module.formatLabels(
          {
            x: _point.xaxis.d2c(point.x),
            y: point.y
          },
          trace,
          fullLayout
        );

        if (fullLayout.xaxis.type === 'linear') {
          labels.xLabel = formatRelativeTimeWithHours(point.x / 1000).replace('<br>', ' ');
        }

        this.hovertemplateData.time  = labels.xLabel;
        this.hovertemplateData.value = labels.yLabel;
        this.hovertemplateData.name  = point.data.name;
        this.hovertemplateData.color = point.data.line.color;
        this.hovertemplateData.unit  = _point.data.series?.valueConfig?.unit;
      } else if (type === 'unhover') {
        if (this.hoverTemplateOverlayRef !== undefined) {
          this.disposeHoverTemplateRef();
          this.hoverTemplateOverlayRef = undefined;
        }
      }
    });
  }


  /**
   * For a given number, calculates the precision for an axis' `tickformat` property
   *
   * @param val The number
   * @returns the precision
   */
  private precisionFn(val: number): number {
    return Math.max(0, 2 - Math.floor(Math.log(val) / Math.LN10 + 0.01)) + 2;
  }

  protected onPlotInitialized(id: string, card: ICard) {
    this.plotlyInstance = this.plotly.getInstanceByDivId('plotlyDiv-' + id);

    if (this.has3DTraces(card.previewTab.graphConfig)) {
      this.freezePlot(id);
    }
    this.plotComplete = true;
    this.plotNextPlot.emit();
  }

  /**
   * Backup the content of the plotly webgl canvases for the given plot,
   * so the plot does not disappear when the webgl context is lost.
   *
   * This is one big workaround for the issue of losing webgl contexts,
   * until https://github.com/plotly/plotly.js/issues/2333#issuecomment-941572511
   * is adequately solved.
   *
   * @param id plot id, to be matched against the data-plotid attribute
   */
  private freezePlot(id: string) {
    const plotlyPlot = document.querySelector(`plotly-plot[data-plotid="${id}"]`);
    const sourceCanvases = plotlyPlot?.querySelectorAll('.gl-container canvas');
    const backupScreenshots = plotlyPlot
      ?.closest('.plot-border')
      ?.querySelector('.backupScreenshot')!;
    backupScreenshots?.replaceChildren();
    sourceCanvases?.forEach(element => {
      const sourceCanvas = element as HTMLCanvasElement;
      if (sourceCanvas !== null) {
        const newScreenshotCanvas = sourceCanvas.cloneNode();
        const newScreenshotCanvasElement = backupScreenshots?.appendChild(newScreenshotCanvas) as HTMLCanvasElement;
        newScreenshotCanvasElement.style.position = 'absolute';
        newScreenshotCanvasElement.style.top =
          `${sourceCanvas.getBoundingClientRect().y - backupScreenshots.getBoundingClientRect().y}px`;
        newScreenshotCanvasElement.style.left =
          `${sourceCanvas.getBoundingClientRect().x - backupScreenshots.getBoundingClientRect().x}px`;

        newScreenshotCanvasElement.getContext('2d')?.drawImage(sourceCanvas, 0, 0);
        sourceCanvas.style.display = 'none';
      }
    });

    // preemptively dispose of 3D plot gl context, so that there are no errors while trying to recover it
    const el = this.plotly.getInstanceByDivId('plotlyDiv-' + id) as any;
    el._fullLayout.scene?._scene?.glplot?.dispose();
  }

  protected hover(evt: any, id: string) {
    this.hoverEvent.next({ type: 'hover', id, evt });
  }

  protected unhover(evt: any, id: string) {
    this.hoverEvent.next({ type: 'unhover', id, evt });
  }
}
